import { useState, useEffect, useMemo } from 'react';
import {
    Box, Typography, Button, Stack, IconButton, Chip,
    Modal, ModalDialog, ModalClose, Input, Select, Option, Divider,
} from '@mui/joy';
import AddRoundedIcon         from '@mui/icons-material/AddRounded';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import LocalOfferRoundedIcon  from '@mui/icons-material/LocalOfferRounded';
import Inventory2RoundedIcon  from '@mui/icons-material/Inventory2Rounded';
import CreditCardRoundedIcon  from '@mui/icons-material/CreditCardRounded';
import PaymentsRoundedIcon    from '@mui/icons-material/PaymentsRounded';
import PointOfSaleRoundedIcon from '@mui/icons-material/PointOfSaleRounded';


const inputSx = {
    bgcolor: 'var(--joy-palette-background-level2)',
    border: '1px solid var(--joy-palette-divider)',
    fontSize: 13, color: 'white',
    '&:focus-within': { borderColor: 'var(--joy-palette-primary-500)' },
};

const MONEY_META = {
    transfer: { label: 'Перевод',  color: '#818cf8', icon: <CreditCardRoundedIcon  sx={{ fontSize: 14 }} /> },
    cash:     { label: 'Наличные', color: '#22c55e', icon: <PaymentsRoundedIcon    sx={{ fontSize: 14 }} /> },
    terminal: { label: 'Терминал', color: '#f59e0b', icon: <PointOfSaleRoundedIcon sx={{ fontSize: 14 }} /> },
};


/**
 * Модалка оформления фантом-чека.
 * Контракт с бэком (новый):
 *   POST /finance/sale-notifications/<id>/process/
 *   { assignments: [{product_id, comment?, discount?}] }
 *   assignments[i] относится к notification.items[i] по индексу.
 *   Одна позиция фантома == один Order + один OrderItem (+ опц. OrderDiscount).
 *
 * Скидка трактуется как информационная — она НЕ вычитает из суммы, потому
 * что value позиции фантома — это уже фактически уплаченная сумма.
 *
 * Props:
 *   notification: объект из APIGetPendingSaleNotifications
 *   products:     список Product (для селекта товара)
 *   discounts:    список Discount (для селекта скидки)
 *   onSubmit({ assignments }): handler из родителя
 */
export default function ProcessSaleModal({ open, notification, products, discounts, loading, onClose, onSubmit }) {
    // Состояние: по одному элементу на каждую позицию фантом-чека
    const [assignments, setAssignments] = useState([]);

    useEffect(() => {
        if (!open || !notification) return;
        setAssignments((notification.items || []).map(item => ({
            // Поля только для отображения (не отправляются):
            _value: parseFloat(item.value),
            _money_type: item.money_type,
            // Поля для отправки:
            product_id: null,
            comment: '',
            discount: null,   // null | {discount_id, name, value, is_percent}
        })));
    }, [open, notification]);

    // ── Хелперы ──────────────────────────────────────────────────
    const setProduct = (idx, product_id) => {
        setAssignments(prev => prev.map((a, i) => i === idx ? {
            ...a,
            product_id: product_id || null,
            // Если выбранный товар сменился, и привязана скидка от старого товара — сбросим скидку
            discount: (a.discount && a.discount.discount_id)
                ? (discounts.find(d => d.id === a.discount.discount_id)?.product === product_id
                    || !discounts.find(d => d.id === a.discount.discount_id)?.product
                    ? a.discount
                    : null)
                : a.discount,
        } : a));
    };

    const setComment = (idx, value) => {
        setAssignments(prev => prev.map((a, i) => i === idx ? { ...a, comment: value } : a));
    };

    const enableDiscount = (idx) => {
        setAssignments(prev => prev.map((a, i) => i === idx ? {
            ...a,
            discount: { discount_id: null, name: '', value: 0, is_percent: false },
        } : a));
    };

    const removeDiscount = (idx) => {
        setAssignments(prev => prev.map((a, i) => i === idx ? { ...a, discount: null } : a));
    };

    const setDiscountField = (idx, field, value) => {
        setAssignments(prev => prev.map((a, i) => i === idx ? {
            ...a,
            discount: { ...a.discount, [field]: value },
        } : a));
    };

    const pickDiscountFromCatalog = (idx, discount_id) => {
        // discount_id === null означает «ручная» (без справочника)
        if (discount_id === null) {
            setAssignments(prev => prev.map((a, i) => i === idx ? {
                ...a,
                discount: { discount_id: null, name: '', value: 0, is_percent: false },
            } : a));
            return;
        }
        const d = discounts.find(x => x.id === discount_id);
        if (!d) return;
        setAssignments(prev => prev.map((a, i) => i === idx ? {
            ...a,
            discount: {
                discount_id: d.id,
                name: d.name,
                value: parseFloat(d.value_from),
                is_percent: !!d.is_percent,
            },
        } : a));
    };

    // ── Валидация ────────────────────────────────────────────────
    const validation = useMemo(() => {
        const missing = assignments
            .map((a, idx) => ({ idx, ok: !!a.product_id }))
            .filter(x => !x.ok);
        return { isValid: missing.length === 0, missingCount: missing.length };
    }, [assignments]);

    // ── Итог (просто сумма по фантому, скидки информационные) ────
    const grandTotal = useMemo(
        () => assignments.reduce((s, a) => s + (a._value || 0), 0),
        [assignments]
    );

    // ── Сабмит ───────────────────────────────────────────────────
    const handleSubmit = () => {
        if (!validation.isValid) return;
        const payload = {
            assignments: assignments.map(a => ({
                product_id: a.product_id,
                comment: (a.comment || '').slice(0, 500),
                discount: a.discount && (a.discount.discount_id || (a.discount.name && parseFloat(a.discount.value) > 0))
                    ? {
                        discount_id: a.discount.discount_id || null,
                        name: a.discount.name || '',
                        value: parseFloat(a.discount.value) || 0,
                        is_percent: !!a.discount.is_percent,
                    }
                    : null,
            })),
        };
        onSubmit(payload);
    };

    if (!open || !notification) return null;

    return (
        <Modal open={open} onClose={onClose}
            slotProps={{ backdrop: { sx: { backdropFilter: 'blur(8px)', bgcolor: 'rgba(0,0,0,0.6)' } } }}
        >
            <ModalDialog sx={{
                bgcolor: 'var(--joy-palette-background-level1)',
                border: '1px solid var(--joy-palette-divider)',
                borderRadius: 'var(--joy-radius-xl)',
                maxWidth: 600, width: '100%',
                p: { xs: 2, sm: 3 },
                maxHeight: '95vh', overflowY: 'auto',
            }}>
                <ModalClose />
                <Typography sx={{ fontSize: 18, fontWeight: 800, mb: 0.5 }}>
                    Оформить чек
                </Typography>
                <Typography sx={{ fontSize: 12, color: 'var(--joy-palette-text-tertiary)', mb: 2 }}>
                    Фотограф: <b>{notification.photographer_name}</b>
                </Typography>

                {/* Комментарий фотографа */}
                {notification.comment && (
                    <Box sx={{
                        p: 1.5, mb: 2,
                        bgcolor: 'rgba(162,38,226,0.08)',
                        border: '1px solid rgba(162,38,226,0.25)',
                        borderRadius: 'var(--joy-radius-md)',
                    }}>
                        <Typography sx={{ fontSize: 10, fontWeight: 700, color: 'var(--joy-palette-primary-400)', textTransform: 'uppercase', letterSpacing: '0.05em', mb: 0.3 }}>
                            Комментарий фотографа:
                        </Typography>
                        <Typography sx={{ fontSize: 12, color: 'var(--joy-palette-text-primary)', whiteSpace: 'pre-wrap' }}>
                            {notification.comment}
                        </Typography>
                    </Box>
                )}

                {/* Привязанный номер (общий для всего фантома, показываем 1 раз) */}
                {notification.payment_number_phone && (
                    <Box sx={{
                        p: 1.5, mb: 2,
                        bgcolor: 'rgba(34,197,94,0.10)',
                        border: '1.5px solid rgba(34,197,94,0.4)',
                        borderRadius: 'var(--joy-radius-md)',
                    }}>
                        <Typography sx={{ fontSize: 11, fontWeight: 700, color: '#22c55e', textTransform: 'uppercase', letterSpacing: '0.05em', mb: 0.5 }}>
                            ✓ Оплата прошла на номер
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                            <Typography sx={{ fontSize: 16, fontWeight: 800, fontFamily: 'monospace', color: 'var(--joy-palette-text-primary)' }}>
                                {notification.payment_number_phone}
                            </Typography>
                            <Chip size="sm" sx={{ bgcolor: 'rgba(34,197,94,0.15)', color: '#22c55e' }}>
                                {notification.payment_number_bank_display}
                            </Chip>
                            {notification.payment_number_owner && (
                                <Typography sx={{ fontSize: 11, color: 'var(--joy-palette-text-tertiary)' }}>
                                    · {notification.payment_number_owner}
                                </Typography>
                            )}
                        </Box>
                        <Typography sx={{ fontSize: 10, color: 'var(--joy-palette-text-tertiary)', mt: 0.5 }}>
                            Привязан ко всем строкам типа «Перевод» в этом чеке
                        </Typography>
                    </Box>
                )}

                {/* ── Позиции фантом-чека ──────────────────────── */}
                <Stack spacing={1.5}>
                    {assignments.map((a, idx) => {
                        const money = MONEY_META[a._money_type] || MONEY_META.cash;
                        // Список доступных скидок: универсальные + привязанные к выбранному товару
                        const availableDiscounts = discounts.filter(d =>
                            d.is_active && (
                                !d.product
                                || (a.product_id && d.product === a.product_id)
                            )
                        );
                        return (
                            <Box key={idx} sx={{
                                p: 1.5,
                                border: a.product_id
                                    ? '1px solid var(--joy-palette-divider)'
                                    : '1px solid rgba(248,113,113,0.4)',
                                borderRadius: 'var(--joy-radius-md)',
                                bgcolor: 'var(--joy-palette-background-level2)',
                            }}>
                                {/* Шапка позиции: сумма + тип оплаты + (если transfer) номер */}
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 1.2 }}>
                                    <Chip
                                        size="sm"
                                        startDecorator={money.icon}
                                        sx={{ bgcolor: `${money.color}22`, color: money.color, fontWeight: 700 }}
                                    >
                                        {a._value.toLocaleString('ru-RU')} ₽ · {money.label}
                                    </Chip>
                                    {a._money_type === 'transfer' && notification.payment_number_phone && (
                                        <Chip size="sm" sx={{
                                            bgcolor: 'rgba(34,197,94,0.10)',
                                            color: '#22c55e',
                                            fontWeight: 600, fontSize: 10,
                                        }}>
                                            {notification.payment_number_bank_display} · {notification.payment_number_phone}
                                        </Chip>
                                    )}
                                </Box>

                                {/* Выбор товара */}
                                <Typography sx={{ fontSize: 10, fontWeight: 700, color: 'var(--joy-palette-text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.05em', mb: 0.4 }}>
                                    <Inventory2RoundedIcon sx={{ fontSize: 12, verticalAlign: 'middle', mr: 0.3 }} />
                                    Товар / услуга <Box component="span" sx={{ color: '#f87171' }}>*</Box>
                                </Typography>
                                <Select
                                    size="sm"
                                    value={a.product_id ?? ''}
                                    onChange={(_, v) => setProduct(idx, v || null)}
                                    placeholder="Выберите товар"
                                    sx={{
                                        ...inputSx,
                                        width: '100%',
                                        ...(a.product_id ? {} : {
                                            borderColor: '#f87171 !important',
                                            bgcolor: 'rgba(248,113,113,0.05) !important',
                                        }),
                                    }}
                                    slotProps={{
                                        listbox: {
                                            sx: {
                                                bgcolor: 'var(--joy-palette-background-level2)',
                                                border: '1px solid var(--joy-palette-divider)',
                                                boxShadow: '0 8px 32px rgba(0,0,0,0.5)',
                                                '& .MuiOption-root': {
                                                    color: 'var(--joy-palette-text-primary)',
                                                    fontSize: 13,
                                                    '&:hover': {
                                                        bgcolor: 'rgba(162,38,226,0.12)',
                                                        color: 'var(--joy-palette-text-primary)',
                                                    },
                                                    '&[aria-selected="true"]': {
                                                        bgcolor: 'rgba(162,38,226,0.2)',
                                                        color: 'var(--joy-palette-primary-300)',
                                                        fontWeight: 600,
                                                    },
                                                },
                                            },
                                        },
                                    }} 
                                >
                                    {products.filter(p => p.is_active).map(p => (
                                        <Option key={p.id} value={p.id}>
                                            {p.name} <Box component="span" sx={{ ml: 1, color: 'var(--joy-palette-text-tertiary)', fontSize: 11 }}>· {p.price} ₽</Box>
                                        </Option>
                                    ))}
                                </Select>

                                {/* Комментарий админа к этой строке (опционально) */}
                                <Input
                                    size="sm"
                                    placeholder="Комментарий к позиции (опц.)"
                                    value={a.comment}
                                    onChange={(e) => setComment(idx, e.target.value.slice(0, 200))}
                                    sx={{ ...inputSx, mt: 0.8 }}
                                />

                                {/* Блок скидки */}
                                <Box sx={{ mt: 1 }}>
                                    {a.discount === null ? (
                                        <Button
                                            size="sm" variant="plain"
                                            startDecorator={<AddRoundedIcon sx={{ fontSize: 14 }} />}
                                            onClick={() => enableDiscount(idx)}
                                            disabled={!a.product_id}
                                            sx={{ fontSize: 11, color: '#f59e0b' }}
                                        >
                                            Добавить скидку
                                        </Button>
                                    ) : (
                                        <Box sx={{
                                            p: 1, borderRadius: 'var(--joy-radius-md)',
                                            bgcolor: 'rgba(245,158,11,0.06)',
                                            border: '1px solid rgba(245,158,11,0.25)',
                                        }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
                                                <Typography sx={{ fontSize: 10, fontWeight: 700, color: '#f59e0b', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                                                    <LocalOfferRoundedIcon sx={{ fontSize: 11, verticalAlign: 'middle', mr: 0.3 }} />
                                                    Скидка
                                                </Typography>
                                                <IconButton size="sm" variant="plain" color="danger" onClick={() => removeDiscount(idx)}>
                                                    <DeleteOutlineRoundedIcon sx={{ fontSize: 14 }} />
                                                </IconButton>
                                            </Box>
                                            <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', flexWrap: 'wrap' }}>
                                                <Select
                                                    size="sm"
                                                    value={a.discount.discount_id ?? 'manual'}
                                                    onChange={(_, v) => pickDiscountFromCatalog(idx, v === 'manual' ? null : v)}
                                                    placeholder="Из справочника"
                                                    sx={{ ...inputSx, flex: 2, minWidth: 180 }}
                                                    slotProps={{
                                                        listbox: {
                                                            sx: {
                                                                bgcolor: 'var(--joy-palette-background-level2)',
                                                                border: '1px solid var(--joy-palette-divider)',
                                                                boxShadow: '0 8px 32px rgba(0,0,0,0.5)',
                                                                '& .MuiOption-root': {
                                                                    color: 'var(--joy-palette-text-primary)',
                                                                    fontSize: 13,
                                                                    '&:hover': {
                                                                        bgcolor: 'rgba(162,38,226,0.12)',
                                                                        color: 'var(--joy-palette-text-primary)',
                                                                    },
                                                                    '&[aria-selected="true"]': {
                                                                        bgcolor: 'rgba(162,38,226,0.2)',
                                                                        color: 'var(--joy-palette-primary-300)',
                                                                        fontWeight: 600,
                                                                    },
                                                                },
                                                            },
                                                        },
                                                    }} 
                                                >
                                                    <Option value="manual">— ручная —</Option>
                                                    {availableDiscounts.map(d => (
                                                        <Option key={d.id} value={d.id}>
                                                            {d.name} ({d.value_from}–{d.value_to}{d.is_percent ? '%' : '₽'})
                                                        </Option>
                                                    ))}
                                                </Select>
                                                {!a.discount.discount_id && (
                                                    <Input
                                                        size="sm"
                                                        placeholder="Название"
                                                        value={a.discount.name}
                                                        onChange={(e) => setDiscountField(idx, 'name', e.target.value.slice(0, 100))}
                                                        sx={{ ...inputSx, flex: 1, minWidth: 120 }}
                                                    />
                                                )}
                                                <Input
                                                    size="sm" type="number"
                                                    value={a.discount.value}
                                                    onChange={(e) => setDiscountField(idx, 'value', parseFloat(e.target.value) || 0)}
                                                    sx={{ ...inputSx, width: 90 }}
                                                    endDecorator={<Typography sx={{ fontSize: 10 }}>{a.discount.is_percent ? '%' : '₽'}</Typography>}
                                                />
                                                {/* Тоггл % / ₽ доступен только для ручной */}
                                                {!a.discount.discount_id && (
                                                    <Button
                                                        size="sm"
                                                        variant={a.discount.is_percent ? 'soft' : 'outlined'}
                                                        onClick={() => setDiscountField(idx, 'is_percent', !a.discount.is_percent)}
                                                        sx={{ minWidth: 40, fontSize: 11 }}
                                                    >
                                                        {a.discount.is_percent ? '%' : '₽'}
                                                    </Button>
                                                )}
                                            </Box>
                                        </Box>
                                    )}
                                </Box>
                            </Box>
                        );
                    })}
                </Stack>

                {/* ── ИТОГ ────────────────────────────────────── */}
                <Box sx={{
                    mt: 2, p: 1.5,
                    bgcolor: 'rgba(34,197,94,0.08)',
                    border: '1px solid rgba(34,197,94,0.3)',
                    borderRadius: 'var(--joy-radius-md)',
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                }}>
                    <Typography sx={{ fontSize: 13, fontWeight: 700, color: 'var(--joy-palette-text-primary)' }}>
                        Итого к оформлению:
                    </Typography>
                    <Typography sx={{ fontSize: 18, fontWeight: 900, color: '#22c55e' }}>
                        {grandTotal.toLocaleString('ru-RU')} ₽
                    </Typography>
                </Box>

                {!validation.isValid && (
                    <Box sx={{
                        mt: 2, p: 1.2,
                        bgcolor: 'rgba(248,113,113,0.08)',
                        border: '1px solid rgba(248,113,113,0.3)',
                        borderRadius: 'var(--joy-radius-md)',
                    }}>
                        <Typography sx={{ fontSize: 12, color: '#f87171', fontWeight: 700 }}>
                            ⚠ Выберите товар/услугу для всех позиций ({validation.missingCount})
                        </Typography>
                        <Typography sx={{ fontSize: 11, color: 'var(--joy-palette-text-tertiary)', mt: 0.3 }}>
                            Без товара чек не оформляется.
                        </Typography>
                    </Box>
                )}

                <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
                    <Button
                        fullWidth
                        onClick={handleSubmit}
                        loading={loading}
                        disabled={!validation.isValid}
                        sx={{
                            background: validation.isValid
                                ? 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)'
                                : 'var(--joy-palette-background-level2)',
                            color: '#fff', fontWeight: 800,
                            opacity: validation.isValid ? 1 : 0.5,
                        }}
                    >
                        {validation.isValid
                            ? `Оформить чек на ${grandTotal.toLocaleString('ru-RU')} ₽`
                            : 'Выберите товары для всех строк'}
                    </Button>
                    <Button variant="outlined" onClick={onClose} sx={{ borderColor: 'var(--joy-palette-divider)' }}>
                        Отмена
                    </Button>
                </Stack>
            </ModalDialog>
        </Modal>
    );
}